import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { siteConfig } from "../siteConfig";
import NavServicesDropdown from "./NavServicesDropdown";
import NavServiceAreasDropdown from "./NavServiceAreasDropdown";
import LogoMark from "./LogoMark";
import { useQuoteModal } from "../context/QuoteModalContext";

const scrollToTop = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo(0, { duration: 0.8 });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

// "Services" and "Service Areas" are replaced by hover dropdowns below
const navLinks = [
  { label: "Home", to: "/" },
  { label: "Gallery", to: "/gallery" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { open: openModal } = useQuoteModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[88px] flex items-center justify-between gap-4">
        {/* Logo — real image when available, SVG mark otherwise */}
        <Link to="/" className="shrink-0" onClick={scrollToTop}>
          {siteConfig.logo ? (
            <img
              src={siteConfig.logo}
              alt={siteConfig.businessName}
              className="h-16 w-auto object-contain"
            />
          ) : (
            <LogoMark scrolled={scrolled} />
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {/* Home */}
          {navLinks.slice(0, 1).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={scrollToTop}
              className={`text-[15px] font-semibold transition-colors hover:opacity-70 ${
                scrolled ? "text-gray-700" : "text-white"
              } ${location.pathname === l.to ? "underline underline-offset-4" : ""}`}
            >
              {l.label}
            </Link>
          ))}

          {/* Services hover dropdown */}
          <NavServicesDropdown scrolled={scrolled} />

          {/* Service Areas hover dropdown */}
          <NavServiceAreasDropdown scrolled={scrolled} />

          {/* Gallery, Blog, Contact */}
          {navLinks.slice(1).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-[15px] font-semibold transition-colors hover:opacity-70 ${
                scrolled ? "text-gray-700" : "text-white"
              } ${location.pathname === l.to ? "underline underline-offset-4" : ""}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={siteConfig.phoneHref}
            className={`flex items-center gap-2 text-[15px] font-semibold ${scrolled ? "text-gray-700" : "text-white"}`}
          >
            <Phone className="w-[18px] h-[18px]" />
            {siteConfig.phone}
          </a>
          <button
            onClick={openModal}
            className="px-6 h-12 flex items-center text-[15px] font-bold text-white rounded-lg transition-opacity hover:opacity-90"
            style={{ backgroundColor: siteConfig.colors.primary }}
          >
            Get Free Quote
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`lg:hidden p-2 ${scrolled ? "text-gray-800" : "text-white"}`}
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-white overflow-hidden transition-all duration-300 ${
          open ? "max-h-[90vh] overflow-y-auto border-t border-gray-100" : "max-h-0 border-t-0"
        }`}
      >
        <div className="px-5 py-5 flex flex-col gap-1">
          {/* Home */}
          {navLinks.slice(0, 1).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={scrollToTop}
              className="py-3 text-gray-800 font-semibold border-b border-gray-100 text-sm"
            >
              {l.label}
            </Link>
          ))}

          {/* Services accordion */}
          <NavServicesDropdown scrolled={scrolled} />

          {/* Service Areas accordion */}
          <NavServiceAreasDropdown scrolled={scrolled} />

          {/* Gallery, Blog, Contact */}
          {navLinks.slice(1).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="py-3 text-gray-800 font-semibold border-b border-gray-100 text-sm"
            >
              {l.label}
            </Link>
          ))}

          <a
            href={siteConfig.phoneHref}
            className="flex items-center gap-2 py-3 font-semibold text-sm border-b border-gray-100"
            style={{ color: siteConfig.colors.primary }}
          >
            <Phone className="w-4 h-4" /> {siteConfig.phone}
          </a>
          <button
            onClick={() => { setOpen(false); openModal(); }}
            className="mt-3 h-12 w-full flex items-center justify-center text-white font-bold rounded-xl"
            style={{ backgroundColor: siteConfig.colors.primary }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
